import PropTypes from 'prop-types';
import React from 'react';
import api from '../../../Api';
import makeConnectTo from '../../util/makeConnectTo';
import { connectToCollaborativeList } from '../../CollaborativeLists/containers/CollaborativeListDataStore';
import { connectToCollaborativeLists } from '../../CollaborativeLists/containers/CollaborativeListsDataStore';
import combineConnectors from '../../util/combineConnectors';
import { connectToUser } from '../../User/containers/UserDataStore';

const TitleContext = React.createContext();

const name = 'titleDataStore';

export const useTitle = () => React.useContext(TitleContext);

export const connectToTitle = makeConnectTo(TitleContext, name);

class TitleDataStore extends React.Component {
	static propTypes = {
		children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
	};

	state = {
		title: {},
		isProductDataLoading: true,
		collaborativeLists: {},
		isCollaborativeListDataLoading: true,
		collections: [],
		isCollectionDataLoading: true,
		review: {},
		isReviewDataLoading: true,
		areReviewsLoading: true,
		communityReviews: [],
		areConversationsLoading: true,
		conversations: [],
	};

	render() {
		const titleDataStore = this.getStore();
		return (
			<TitleContext.Provider value={titleDataStore}>
				{typeof this.props.children === 'function'
					? this.props.children(titleDataStore)
					: this.props.children}
			</TitleContext.Provider>
		);
	}

	componentDidMount() {
		const {
			doNotLoad,
			userDataStore: { isAnonymous },
		} = this.props;

		if (!doNotLoad) {
			this.fetchTitle();
			this.fetchCollaborativeListData();
			this.fetchCommunityReviews();
			this.fetchConversations();
		}

		if (!isAnonymous()) {
			this.fetchCollectionData();
			this.fetchReview();
		} else {
			this.setState({
				isCollectionDataLoading: false,
				isReviewDataLoading: false,
			});
		}
	}

	componentWillUnmount() {
		if (this.titleFetch && this.titleFetch.cancel) {
			this.titleFetch.cancel();
		}
		if (this.collaborativeListFetch && this.collaborativeListFetch.cancel) {
			this.collaborativeListFetch.cancel();
		}
		if (this.reviewFetch && this.reviewFetch.cancel) {
			this.reviewFetch.cancel();
		}
		if (this.collectionFetch && this.collectionFetch.cancel) {
			this.collectionFetch.cancel();
		}
		if (this.communityReviewsFetch && this.communityReviewsFetch.cancel) {
			this.communityReviewsFetch.cancel();
		}
		if (this.conversationsFetch && this.conversationsFetch.cancel) {
			this.conversationsFetch.cancel();
		}
	}

	getStore = (state = this.state) => ({
		state,
		getSku: () => state.title.sku,
		refresh: () => this.refreshTitle(),
		refreshReview: () => this.refreshReview(),
		refreshCollaborativeListData: () => this.refreshCollaborativeListData(),
		refreshCollectionData: () => this.refreshCollectionData(),
		refreshCommunityReviews: () => this.refreshCommunityReviews(),
		refreshConversations: () => this.refreshConversations(),
		addTitles: listId => this.addTitles(listId),
		getName: () => this.getName(state),
	});

	refreshTitle = () => {
		this.setState({
			isProductDataLoading: true,
		});
		this.fetchTitle();
		return this.titleFetch.promise;
	};

	refreshReview = () => {
		this.fetchReview();
	};

	fetchTitle = () => {
		const { sku } = this.props;
		const request = api.cancelableGet(
			`/api/v1/products/${sku}/images,links,descriptions`,
		);
		request.promise.then(this.cacheApiResponse).catch(err => {
			console.error(err);
			this.setState({ isProductDataLoading: false });
		});
		this.titleFetch = request;
	};

	cacheApiResponse = title => {
		this.setState({ title, isProductDataLoading: false });
	};

	refreshCollaborativeListData = () => {
		this.setState({
			isCollaborativeListDataLoading: true,
		});
		this.fetchCollaborativeListData();
		return this.collaborativeListFetch.promise;
	};

	fetchCollaborativeListData = () => {
		const { sku } = this.props;
		const request = api.cancelableGet(
			`/api/v1/collaborativeTitleLists/titles/${sku}`,
		);
		request.promise.then(this.cacheCollaborativeListApiResponse);
		this.collaborativeListFetch = request;
	};

	cacheCollaborativeListApiResponse = collaborativeLists => {
		this.setState({
			collaborativeLists,
			isCollaborativeListDataLoading: false,
		});
	};

	refreshCollectionData = () => {
		this.setState({
			isCollectionDataLoading: true,
		});
		this.fetchCollectionData();
		return this.collectionFetch.promise;
	};

	fetchCollectionData = () => {
		const { sku } = this.props;
		const request = api.cancelableGet(
			`/api/v1/me/collections/title/${sku}`,
		);
		request.promise.then(this.cacheCollectionApiResponse);
		this.collectionFetch = request;
	};

	cacheCollectionApiResponse = collections => {
		this.setState({ collections, isCollectionDataLoading: false });
	};

	refreshCommunityReviews = () => {
		this.setState({
			areReviewsLoading: true,
		});
		this.fetchCommunityReviews();
		return this.communityReviewsFetch.promise;
	};

	fetchCommunityReviews = () => {
		const { sku, communityId } = this.props;

		if (!sku) return;

		const request = api.cancelableGet(
			`/api/v1/affiliations/${communityId ?? 0}/${sku}/reviews`,
		);
		request.promise.then(this.cacheCommunityReviewsApiResponse);
		this.communityReviewsFetch = request;
	};

	cacheCommunityReviewsApiResponse = res => {
		const results = res.reviews.filter(rev => rev.rating);
		var reviews = [];
		results.forEach((review, index) => {
			const user =
				review.appUserId in res.usersById &&
				res.usersById[review.appUserId];
			const orgId = user?.orgID?.toLowerCase();

			reviews.push({
				sku: review.sku,
				createdDate: review.createdDate,
				hasReviewText: review.hasReviewText,
				rating: review.rating,
				text: review.text,
				userAvatar:
					review.appUserId in res.userAvatarsByAppUserId &&
					res.userAvatarsByAppUserId[review.appUserId],
				user: user,
				organization:
					orgId &&
					orgId in res.organizationsById &&
					res.organizationsById[orgId],
			});
		});

		reviews = reviews.sort((a, b) => b.reviews - a.reviews);

		this.setState({
			reviews: reviews,
			areReviewsLoading: false,
		});
	};

	addTitles(listId) {
		var listItems = [];
		listItems.push({
			listId: listId,
			sku: this.props.sku,
		});
		api.post(
			`/api/v1/collaborativeTitleLists/${listId}/items/add`,
			listItems,
		).then(() => {
			this.refreshCollaborativeListData();
			this.props.collaborativeListDataStore &&
				this.props.collaborativeListDataStore.refresh();
			this.props.collaborativeListsDataStore &&
				this.props.collaborativeListsDataStore.refresh();
		});
	}

	fetchReview = () => {
		const { sku } = this.props;
		const request = api.cancelableGet(`/api/v1/me/reviews/${sku}/review`);
		request.promise.then(this.cacheApiReviewResponse);
		this.reviewFetch = request;
	};

	cacheApiReviewResponse = review => {
		this.setState({ review, isReviewDataLoading: false });
	};

	refreshConversations = () => {
		this.setState({
			areConversationsLoading: true,
		});
		this.fetchConversations();
		return this.conversationsFetch.promise;
	};

	fetchConversations = () => {
		const { sku } = this.props;
		const request = api.cancelableGet(`/api/v1/conversations/title/${sku}`);
		request.promise.then(this.cacheConversationsApiResponse);
		this.conversationsFetch = request;
	};

	cacheConversationsApiResponse = conversations => {
		this.setState({ conversations, areConversationsLoading: false });
	};

	getName = (state = this.state) => {
		return state.title.name;
	};
}

const connectors = [
	connectToCollaborativeLists,
	connectToCollaborativeList,
	connectToUser,
];

export default combineConnectors(connectors)(TitleDataStore);
